import { Check, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext';
import './ModelSelector.css';

const ModelSelector = ({ selectedModels, onSelectionChange, disabled = false }) => {
  const { authenticatedFetch } = useAuth();
  const [vehicleModels, setVehicleModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  // Cargar modelos de vehículos (con auth para admin)
  const loadVehicleModels = async () => {
    if (!authenticatedFetch) return;
    try {
      setLoading(true);
      const response = await authenticatedFetch(buildApiUrl(API_ENDPOINTS.VEHICLE_MODELS));
      if (response.ok) {
        const data = await response.json();
        setVehicleModels(Array.isArray(data) ? data : []);
      } else {
        console.error('Error al cargar modelos de vehículos');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVehicleModels();
  }, [authenticatedFetch]);

  // Calcular posición del dropdown
  const calculateDropdownPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 380; // dropdown más alto (~24rem)
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      let top = rect.bottom + scrollTop + 4;
      
      // Si no hay espacio suficiente abajo, mostrar arriba
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        top = rect.top + scrollTop - dropdownHeight - 4;
      }
      
      setDropdownPosition({
        top: top,
        left: rect.left + scrollLeft,
        width: rect.width
      });
    }
  };

  // Actualizar posición cuando se abre el dropdown
  useEffect(() => {
    if (showDropdown) {
      calculateDropdownPosition();
      const handleResize = () => calculateDropdownPosition();
      const handleScroll = () => calculateDropdownPosition();
      
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [showDropdown]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  // Lista unificada: modelos del API + modelos ya seleccionados que no estén en el API
  // (así al editar un accesorio se ven siempre los seleccionados en el dropdown)
  const allModels = (() => {
    const byId = new Map();
    vehicleModels.forEach(m => byId.set(m.id, m));
    (selectedModels || []).forEach(m => {
      if (m && m.id && !byId.has(m.id)) byId.set(m.id, { ...m, brand: m.brand || '' });
    });
    return Array.from(byId.values());
  })();

  // Filtrar modelos por término de búsqueda
  const filteredModels = allModels.filter(model =>
    (model.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (model.brand || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Comparar por id de forma robusta (por si vienen number vs string del API)
  const isModelSelected = (model) =>
    (selectedModels || []).some(selected => selected && model && String(selected.id) === String(model.id));

  // Manejar selección de modelo
  const handleModelToggle = (model) => {
    if (disabled) return;
    
    const isSelected = isModelSelected(model);
    
    if (isSelected) {
      // Remover modelo
      onSelectionChange((selectedModels || []).filter(selected => String(selected.id) !== String(model.id)));
    } else {
      // Agregar modelo
      onSelectionChange([...(selectedModels || []), model]);
    }
  };

  // Manejar selección de todos los modelos
  const handleSelectAll = () => {
    if (disabled) return;
    const current = selectedModels || [];
    if (current.length === filteredModels.length) {
      // Deseleccionar todos
      onSelectionChange([]);
    } else {
      // Seleccionar todos los filtrados
      onSelectionChange([...filteredModels]);
    }
  };

  // Remover modelo seleccionado
  const handleRemoveModel = (modelId) => {
    if (disabled) return;
    onSelectionChange((selectedModels || []).filter(selected => String(selected.id) !== String(modelId)));
  };

  const selectedCount = (selectedModels || []).length;
  const isAllSelected = selectedCount === filteredModels.length && filteredModels.length > 0;
  const isPartiallySelected = selectedCount > 0 && selectedCount < filteredModels.length;

  if (loading) {
    return (
      <div className="model-selector">
        <div className="loading">Cargando modelos...</div>
      </div>
    );
  }

  return (
    <div className="model-selector">
      <label className="form-label">Modelos de Bicicletas Compatibles</label>
      
      {/* Modelos seleccionados */}
      {selectedCount > 0 && (
        <div className="selected-models">
          <div className="selected-models-header">
            <span>Modelos seleccionados ({selectedCount})</span>
            {!disabled && (
              <button
                type="button"
                className="clear-all-btn"
                onClick={() => onSelectionChange([])}
              >
                Limpiar todo
              </button>
            )}
          </div>
          <div className="selected-models-list">
            {selectedModels.map(model => (
              <div key={model.id} className="selected-model-item">
                <span className="model-name">{model.name}</span>
                {!disabled && (
                  <button
                    type="button"
                    className="remove-model-btn"
                    onClick={() => handleRemoveModel(model.id)}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selector de modelos */}
      <div className="model-dropdown">
        <div 
          ref={triggerRef}
          className="dropdown-trigger"
          onClick={() => !disabled && setShowDropdown(!showDropdown)}
        >
          <span>
            {selectedCount === 0 
              ? 'Seleccionar modelos...' 
              : `${selectedCount} modelo(s) seleccionado(s)`
            }
          </span>
          <div className={`dropdown-arrow ${showDropdown ? 'open' : ''}`}>▼</div>
        </div>

        {showDropdown && (
          <div 
            ref={dropdownRef}
            className="dropdown-content"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`
            }}
          >
            {/* Búsqueda */}
            <div className="search-container">
              <Search size={16} />
              <input
                type="text"
                placeholder="Buscar modelos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Botón seleccionar todos */}
            {filteredModels.length > 0 && (
              <div className="select-all-container">
                <button
                  type="button"
                  className={`select-all-btn ${isAllSelected ? 'selected' : ''} ${isPartiallySelected ? 'partial' : ''}`}
                  onClick={handleSelectAll}
                >
                  <Check size={16} />
                  {isAllSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
                </button>
              </div>
            )}

            {/* Lista de modelos */}
            <div className="models-list">
              {filteredModels.length === 0 ? (
                <div className="no-results">
                  {searchTerm ? 'No se encontraron modelos' : 'No hay modelos disponibles'}
                </div>
              ) : (
                filteredModels.map(model => {
                  const isSelected = isModelSelected(model);
                  
                  return (
                    <div
                      key={model.id}
                      className={`model-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleModelToggle(model)}
                    >
                      <div className="model-checkbox">
                        {isSelected && <Check size={16} />}
                      </div>
                      <div className="model-info">
                        <div className="model-name">{model.name}</div>
                        <div className="model-category">{model.category?.name || 'Sin categoría'}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default ModelSelector;
